import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Wrench } from 'lucide-react';

const BACKEND_URL = window.location.origin;

// Public routes that should remain accessible during maintenance
const PUBLIC_ROUTES = ['/', '/login', '/register', '/forgot-password'];

export default function MaintenanceGuard({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [maintenance, setMaintenance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/config/maintenance`);
        setMaintenance(response.data);
        
        // If maintenance is enabled and user is a customer/provider, force logout
        if (response.data?.enabled && user) {
          const allowedRoles = response.data.allowed_roles || ['admin', 'manager'];
          if (!allowedRoles.includes(user.role)) {
            // Force logout for non-allowed roles
            logout();
            navigate('/login', { 
              state: { 
                message: 'The site is currently under maintenance. Please try again later.' 
              }
            });
          }
        }
      } catch (error) {
        // If API fails, assume no maintenance
        setMaintenance({ enabled: false });
      } finally {
        setIsLoading(false);
      }
    };

    checkMaintenance();
    // Check every 2 minutes
    const interval = setInterval(checkMaintenance, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, logout, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Check if maintenance mode is enabled
  if (maintenance?.enabled) {
    const allowedRoles = maintenance.allowed_roles || ['admin', 'manager'];
    const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);
    
    // Allow access for admin and manager roles
    if (user && allowedRoles.includes(user.role)) {
      return children;
    }
    
    // Allow public routes (landing page, login, register) to remain accessible
    if (isPublicRoute) {
      return children;
    }
    
    // Show maintenance page for non-allowed users trying to access protected routes
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Wrench className="w-10 h-10 text-amber-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-slate-900 mb-3">
            {maintenance.title || 'Under Maintenance'}
          </h1>
          
          <p className="text-slate-600 mb-6">
            {maintenance.message || "We're currently performing scheduled maintenance. Please check back soon."}
          </p>
          
          <div className="flex items-center justify-center gap-2 text-amber-600 bg-amber-50 rounded-xl p-3">
            <AlertTriangle className="w-5 h-5" />
            <span className="text-sm font-medium">We'll be back shortly!</span>
          </div>
          
          {user && (
            <p className="text-xs text-slate-400 mt-6">
              Logged in as {user.email} ({user.role})
            </p>
          )}
        </div>
      </div>
    );
  }

  return children;
}
